import {
  SiteConnectionDetail,
  WireGuardTunnel,
} from "../troubleshootingInterfaces";

/**
 * Which WireGuard tunnel carries a site (verified with redesign-be against
 * admin and the gateway route manager, 2026-09-27).
 *
 * `detail.wireguard` is the site's WireGuard GATEWAY (availability zone), not
 * a tunnel: ids of the two tables collide by accident. Admin stores no
 * site-to-tunnel link; the connector runs on a node in the gateway's zone,
 * whose route manager routes every tunnel by destination subnet. Subnets are
 * unique fleet-wide, so the tunnel is the one whose subnet holds an address
 * the connector dials: the site's server / management / event server, or a
 * camera's host. A tunnel's group plays no part (Brian's Backyard rides
 * "Brian House" from another group).
 */
const ipv4 = (value: string): number | null => {
  const match = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(
    value.trim()
  );
  if (!match) return null;
  const parts = match.slice(1).map(Number);
  if (parts.some((part) => part > 255)) return null;
  return (
    ((parts[0] << 24) >>> 0) + (parts[1] << 16) + (parts[2] << 8) + parts[3]
  );
};

export const cidrContains = (cidr: string, address: string): boolean => {
  const [base, bitsText] = cidr.trim().split("/");
  const baseValue = ipv4(base);
  const target = ipv4(address);
  if (baseValue == null || target == null) return false;
  const bits = bitsText == null ? 32 : Number(bitsText);
  if (!Number.isInteger(bits) || bits < 0 || bits > 32) return false;
  const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0;
  return (baseValue & mask) >>> 0 === (target & mask) >>> 0;
};

/**
 * The ranges a tunnel carries, as admin's `scan_targets_for_tunnel` judges
 * them: its subnets, or, when it has none, its own client tunnel address.
 * That is the single-box NVR case: WireGuard runs on the recorder, so the
 * client address is the device.
 */
export const tunnelRoutedRanges = (tunnel: WireGuardTunnel): string[] => {
  const subnets = (tunnel.subnets ?? []).filter(
    (cidr) => typeof cidr === "string" && cidr.trim()
  );
  if (subnets.length) return subnets;
  const own = (tunnel.client_tunnel_ip ?? "").trim();
  return own ? [own] : [];
};

/** A tunnel's client address without its /32, when it is the device itself. */
export const tunnelDeviceAddress = (tunnel: WireGuardTunnel): string | null =>
  (tunnel.subnets ?? []).some((cidr) => String(cidr).trim())
    ? null
    : (tunnel.client_tunnel_ip ?? "").trim().replace(/\/32$/, "") || null;

/** Addresses the connector dials for a site, deduplicated. */
export const siteHosts = (
  detail: SiteConnectionDetail | null,
  cameraHosts: (string | null | undefined)[] = []
): string[] => {
  if (!detail) return [];
  const all = [
    detail.server_ip,
    detail.management_server_ip,
    detail.event_server_ip,
    ...cameraHosts,
  ]
    .map((host) => String(host ?? "").trim())
    .filter(Boolean);
  return Array.from(new Set(all));
};

export interface SiteTunnelResolution {
  tunnels: WireGuardTunnel[];
  /** Hostnames: cannot be matched to a subnet without resolving them. */
  unresolved: string[];
}

export const resolveSiteTunnels = (
  detail: SiteConnectionDetail | null,
  cameraHosts: (string | null | undefined)[],
  tunnels: WireGuardTunnel[]
): SiteTunnelResolution => {
  if (!detail || detail.wireguard == null)
    return { tunnels: [], unresolved: [] };
  const hosts = siteHosts(detail, cameraHosts);
  const matched = new Map<number, WireGuardTunnel>();
  const unresolved: string[] = [];
  hosts.forEach((host) => {
    if (ipv4(host) == null) {
      unresolved.push(host);
      return;
    }
    const tunnel = tunnels.find((t) =>
      tunnelRoutedRanges(t).some((cidr) => cidrContains(cidr, host))
    );
    if (tunnel) matched.set(tunnel.id, tunnel);
  });
  return { tunnels: Array.from(matched.values()), unresolved };
};

/**
 * What the WireGuard tab says when no tunnel carries the selected site (QA
 * SC-16c, ENG-2187). A site with a WireGuard gateway is offered in the
 * tab's picker and the scope note says it connects via WireGuard, so "does
 * not connect through a WireGuard tunnel" contradicts both. Say instead that
 * none of its addresses is in a tunnel's subnet: a wrong address, or a tunnel
 * in a group the user can't see (Brian's Backyard rides "Brian House" from
 * another group).
 */
export const noSiteTunnelMessage = (
  detail: SiteConnectionDetail | null,
  cameraHosts: (string | null | undefined)[] = []
): string => {
  if (!detail || detail.wireguard == null)
    return "This site does not connect through a WireGuard tunnel.";
  const hosts = siteHosts(detail, cameraHosts);
  if (hosts.length === 0)
    return "This site is set up to connect through WireGuard, but it has no server or camera addresses yet, so no tunnel can be matched to it.";
  const shown = hosts.slice(0, 4);
  const more = hosts.length - shown.length;
  const listed =
    more > 0 ? `${shown.join(", ")} and ${more} more` : listAddresses(shown);
  const hostnames = hosts.filter((host) => ipv4(host) == null);
  return (
    `This site is set up to connect through WireGuard, but none of the addresses it connects to (${listed}) is inside the subnet of a tunnel your account can see. ` +
    "Check those addresses against the tunnel's subnets on the WireGuard page." +
    (hostnames.length
      ? ` Hostnames (${listAddresses(
          hostnames.slice(0, 4)
        )}) can't be matched to a subnet.`
      : "")
  );
};

/** A WireGuard gateway from `GET wireguard/` (appStore.wireGuardOptions). */
export interface GatewayZone {
  id: number;
  zone?: string | null;
}

const zoneOf = (
  gateways: GatewayZone[],
  gatewayId: number | null | undefined
): string | null => {
  if (gatewayId == null) return null;
  const zone = gateways.find((g) => g.id === gatewayId)?.zone;
  return zone && zone.trim() ? zone.trim() : null;
};

export interface TunnelZoneMismatch {
  tunnel: WireGuardTunnel;
  /** The zone of the site's gateway (`Customer.wireguard`), where its connector runs. */
  siteZone: string;
  tunnelZone: string;
  /** The site's addresses this tunnel routes that no tunnel in the site's zone does. */
  addresses: string[];
}

/**
 * Tunnels that route one of the site's addresses from a gateway in another
 * availability zone (ENG-2059). The connector runs in its gateway's zone and
 * only that zone's tunnels are routed on its node, so it cannot reach those
 * addresses through them. An address that a tunnel in the site's own zone
 * also routes is reachable and not reported. Nothing is reported when
 * either zone is unknown.
 */
export const tunnelZoneMismatches = (
  detail: SiteConnectionDetail | null,
  cameraHosts: (string | null | undefined)[],
  tunnels: WireGuardTunnel[],
  gateways: GatewayZone[]
): TunnelZoneMismatch[] => {
  if (!detail) return [];
  const siteZone = zoneOf(gateways, detail.wireguard);
  if (!siteZone) return [];
  const byTunnel = new Map<number, TunnelZoneMismatch>();
  siteHosts(detail, cameraHosts)
    .filter((host) => ipv4(host) != null)
    .forEach((host) => {
      const routing = tunnels.filter((t) =>
        tunnelRoutedRanges(t).some((cidr) => cidrContains(cidr, host))
      );
      if (routing.some((t) => zoneOf(gateways, t.server) === siteZone)) return;
      routing.forEach((tunnel) => {
        const tunnelZone = zoneOf(gateways, tunnel.server);
        if (!tunnelZone) return;
        const entry = byTunnel.get(tunnel.id) ?? {
          tunnel,
          siteZone,
          tunnelZone,
          addresses: [],
        };
        entry.addresses.push(host);
        byTunnel.set(tunnel.id, entry);
      });
    });
  return Array.from(byTunnel.values());
};

/** "a", "a and b", "a, b and c". */
export const listAddresses = (addresses: string[]): string =>
  addresses.length <= 1
    ? addresses.join("")
    : `${addresses.slice(0, -1).join(", ")} and ${
        addresses[addresses.length - 1]
      }`;
